/**
 * A-01 invariant: spawn proposals require a founder Ed25519 signature.
 * The founder pastes their signature from FounderSignBar; it is sent
 * as X-Founder-Signature on the approve POST.
 */

import { useState } from 'react';
import { Navigate, Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';
import { apiFetch } from '@/api/http';
import { fmtCents, proposalStatusColor } from './AlphaOmegaDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

interface SpawnProposalResponse {
  proposal_id: string;
  company_id: string;
  title: string;
  description: string | null;
  budget_cents: number;
  status: string;
  created_at: string;
}

// ── Mutations ─────────────────────────────────────────────────────────────────

async function postApprove(proposalId: string, sig: string, reason?: string): Promise<void> {
  const token = await getAccessToken();
  const body = { reason: reason ?? null, nonce: crypto.randomUUID() };
  const resp = await fetch(`${apiBaseUrl()}/alpha-omega/spawn-proposals/${proposalId}/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Founder-Signature': sig,
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
}

async function postReject(proposalId: string, sig: string, reason: string): Promise<void> {
  const token = await getAccessToken();
  const body = { reason, nonce: crypto.randomUUID() };
  const resp = await fetch(`${apiBaseUrl()}/alpha-omega/spawn-proposals/${proposalId}/reject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Founder-Signature': sig,
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  width: '100%', boxSizing: 'border-box' as const,
};

function hexToRgb(hex: string): string {
  return `${parseInt(hex.slice(1,3),16)},${parseInt(hex.slice(3,5),16)},${parseInt(hex.slice(5,7),16)}`;
}

function btnStyle(color: string): React.CSSProperties {
  return {
    background: `rgba(${hexToRgb(color)},0.12)`,
    border: `1px solid rgba(${hexToRgb(color)},0.4)`,
    borderRadius: 4, color, fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', fontWeight: 600, padding: '6px 14px',
    cursor: 'pointer', letterSpacing: '0.04em',
  };
}

// ── Toast ──────────────────────────────────────────────────────────────────────

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: ok ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
      border: `1px solid ${ok ? '#34d399' : '#f87171'}`,
      borderRadius: 6, color: ok ? '#34d399' : '#f87171',
      fontFamily: 'var(--omega-mono)', fontSize: '9pt',
      padding: '10px 20px', zIndex: 9999, direction: 'ltr',
    }}>
      {msg}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AlphaOmegaSpawnProposalDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { proposal_id = '' } = useParams<{ proposal_id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isPending, isError } = useQuery({
    queryKey: ['alpha-omega', 'spawn-proposal', proposal_id],
    queryFn: () => apiFetch<SpawnProposalResponse>(`/alpha-omega/spawn-proposals/${proposal_id}`),
    refetchInterval: 30_000,
    enabled: !!proposal_id,
  });

  const [sigInput,     setSigInput]     = useState('');
  const [rejectOpen,   setRejectOpen]   = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [acting,       setActing]       = useState(false);
  const [toast,        setToast]        = useState<{ msg: string; ok: boolean } | null>(null);

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleApprove() {
    if (!sigInput.trim()) return;
    setActing(true);
    try {
      await postApprove(proposal_id, sigInput.trim());
      await queryClient.invalidateQueries({ queryKey: ['alpha-omega', 'spawn-proposals'] });
      showToast('Proposal approved', true);
      setTimeout(() => navigate('/omega/alpha-omega/spawn-proposals'), 1500);
    } catch {
      showToast('Approval error — check signature', false);
    } finally {
      setActing(false);
    }
  }

  async function handleReject() {
    if (!sigInput.trim() || !rejectReason.trim()) return;
    setActing(true);
    try {
      await postReject(proposal_id, sigInput.trim(), rejectReason.trim());
      await queryClient.invalidateQueries({ queryKey: ['alpha-omega', 'spawn-proposals'] });
      showToast('Proposal rejected', true);
      setTimeout(() => navigate('/omega/alpha-omega/spawn-proposals'), 1500);
    } catch {
      showToast('Rejection error — try again', false);
    } finally {
      setActing(false);
    }
  }

  const crumbLabel = data?.title.slice(0, 30) ?? proposal_id.slice(0, 12);
  const isPending_ = data?.status === 'pending';

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',        to: '/omega' },
        { label: 'Alpha-Omega',  to: '/omega/alpha-omega' },
        { label: 'Proposals Spawn',  to: '/omega/alpha-omega/spawn-proposals' },
        { label: crumbLabel },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', direction: 'ltr', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 480 }}>
          {data?.title ?? 'Loading…'}
        </h1>
        {data && (
          <span style={{
            fontSize: '7.5pt', padding: '2px 8px',
            border: `1px solid ${proposalStatusColor(data.status)}`,
            color: proposalStatusColor(data.status),
            letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3,
          }}>
            {data.status}
          </span>
        )}
        <Link
          to="/omega/alpha-omega/spawn-proposals"
          style={{
            marginLeft: 'auto', background: 'rgba(125,211,252,0.12)',
            border: '1px solid rgba(125,211,252,0.35)', borderRadius: 4,
            color: '#7dd3fc', fontFamily: 'var(--omega-mono)', fontSize: '8.5pt',
            fontWeight: 600, padding: '5px 12px', textDecoration: 'none',
          }}
        >
          ← Back to queue
        </Link>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading proposal.</div>}

      {data && (
        <>
          <div style={{ display: 'flex', gap: 20, padding: '10px 0 14px', borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.08))', flexWrap: 'wrap', direction: 'ltr' }}>
            <MetaChip label="Company"   value={data.company_id.slice(0, 16)} />
            <MetaChip label="Budget"  value={fmtCents(data.budget_cents)} />
            <MetaChip label="Created"   value={data.created_at.slice(0, 10)} />
          </div>

          {data.description && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8, direction: 'ltr' }}>
                Description
              </div>
              <pre style={{
                background: 'rgba(255,255,255,0.04)', border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
                borderRadius: 5, padding: '14px 16px', fontSize: '8.5pt',
                lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                color: 'var(--omega-ink)', direction: 'ltr',
              }}>
                {data.description}
              </pre>
            </div>
          )}

          {/* A-01: approve / reject require founder signature */}
          {isPending_ && (
            <div style={{ marginTop: 24, padding: '18px 0 0', borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.1))' }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 6, direction: 'ltr' }}>
                  Signature (A-01 — FounderSignBar ← Cmd+Shift+S)
                </label>
                <input
                  type="text"
                  placeholder="Paste the signature here…"
                  value={sigInput}
                  onChange={e => setSigInput(e.target.value)}
                  style={{ ...inputStyle, direction: 'ltr' }}
                  dir="ltr"
                />
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                <button
                  disabled={!sigInput.trim() || acting}
                  onClick={handleApprove}
                  style={{ ...btnStyle('#34d399'), opacity: (!sigInput.trim() || acting) ? 0.4 : 1, direction: 'ltr' }}
                >
                  Approve proposal
                </button>
                <button
                  onClick={() => setRejectOpen(v => !v)}
                  style={{ ...btnStyle('#f87171'), direction: 'ltr' }}
                >
                  Reject
                </button>
              </div>

              {rejectOpen && (
                <div style={{ marginBottom: 14, padding: '14px', background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 5 }}>
                  <label style={{ fontSize: '8.5pt', color: '#f87171', display: 'block', marginBottom: 6, direction: 'ltr' }}>
                    Rejection reason
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical', direction: 'ltr' }}
                    dir="ltr"
                    placeholder="State the reason for rejection…"
                  />
                  <button
                    disabled={!sigInput.trim() || !rejectReason.trim() || acting}
                    onClick={handleReject}
                    style={{ ...btnStyle('#f87171'), marginTop: 8, opacity: (!sigInput.trim() || !rejectReason.trim() || acting) ? 0.4 : 1, direction: 'ltr' }}
                  >
                    Confirm rejection
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </div>
  );
}

function MetaChip({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: '9pt', color: 'var(--omega-ink)' }}>{value}</span>
    </div>
  );
}
